import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import SiteFooter from '../components/SiteFooter';

const SECTIONS = [
  {
    n: '1',
    t: 'Introduction',
    p: 'Around the World AI ("we", "us", "our") respects your privacy. This Privacy Policy explains what data we collect, how we use it, and the choices you have. This policy applies to our website, apps, and services.',
  },
  {
    n: '2',
    t: 'Data we collect',
    p: 'We collect: (a) account data — name, email, profile picture from Google Sign-In; (b) content data — prompts you send to the AI, files you upload, chats you save; (c) usage data — pages visited, features used, device/browser info, IP address; (d) payment data — handled by Stripe (we never see your full card details); (e) email leads submitted via the free-tools email gate.',
  },
  {
    n: '3',
    t: 'How we use your data',
    p: 'We use your data to (a) provide and improve the Service, (b) personalize your experience, (c) process payments, (d) send transactional emails (welcome, receipts, product updates), (e) analyze usage patterns to fix bugs and prioritize features, (f) comply with legal obligations.',
  },
  {
    n: '4',
    t: 'AI processing',
    p: 'Prompts and conversations you send are transmitted to third-party AI providers (Anthropic, OpenAI, Google) solely to generate a response. We do not train our own models on your data. Per their policies, these providers may retain your prompts for a limited period for abuse detection but do not use them to train their models when accessed via API.',
  },
  {
    n: '5',
    t: 'Data sharing',
    p: 'We do NOT sell your personal data. We share limited data with (a) service providers: Stripe (payments), SMTP provider (transactional email), Anthropic/OpenAI/Google (AI processing), hosting providers; (b) authorities when required by law; (c) new owners in the case of a merger or acquisition (with notice to you).',
  },
  {
    n: '6',
    t: 'Cookies',
    id: 'cookies',
    p: 'We use only essential cookies for authentication and session management. We do not use tracking cookies for advertising. You can disable cookies in your browser, but authentication will not work without them.',
  },
  {
    n: '7',
    t: 'Data retention',
    p: 'Account data is kept while your account is active. Chat history is kept for 12 months by default. You can delete individual chats or your whole account at any time. Payment records are kept for 7 years as required by tax law. Deleted data is purged within 30 days.',
  },
  {
    n: '8',
    t: 'Your rights',
    p: 'You have the right to (a) access your data, (b) correct inaccurate data, (c) delete your account and data, (d) export your data in a portable format, (e) object to processing, (f) lodge a complaint with your local data protection authority. To exercise any of these rights, email hello@aroundtheworldai.com.',
  },
  {
    n: '9',
    t: 'International transfers',
    p: 'Your data may be processed in countries outside your own, including the United States and the European Union, where our providers operate. We rely on Standard Contractual Clauses and adequacy decisions to ensure appropriate protection.',
  },
  {
    n: '10',
    t: 'Security',
    p: 'We use industry-standard measures: TLS encryption in transit, encrypted storage, access controls, audit logging. No system is 100% secure — but we work hard to protect your data and will notify you of material breaches within 72 hours.',
  },
  {
    n: '11',
    t: 'Children',
    p: 'The Service is not intended for anyone under 16. If we learn we have collected data from a child under 16, we will delete it promptly.',
  },
  {
    n: '12',
    t: 'DPA',
    id: 'dpa',
    p: 'Business plan customers can request a Data Processing Agreement (DPA) by emailing hello@aroundtheworldai.com. Our standard DPA covers GDPR obligations.',
  },
  {
    n: '13',
    t: 'Changes to this policy',
    p: 'We may update this Privacy Policy from time to time. Material changes will be posted here and communicated via email at least 14 days before taking effect.',
  },
  {
    n: '14',
    t: 'Contact',
    p: 'For privacy questions, contact our Data Protection contact at hello@aroundtheworldai.com.',
  },
];

export default function PrivacyPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#030303] text-white font-sans relative overflow-hidden">
      <div
        aria-hidden
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full blur-3xl opacity-15 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #FF007F 0%, transparent 70%)' }}
      />

      <header className="relative z-10 border-b border-white/[0.06] bg-black/40 backdrop-blur-xl">
        <div className="mx-auto max-w-5xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              data-testid="privacy-back-btn"
              className="text-white/60 hover:text-white transition"
            >
              <ArrowLeft size={18} />
            </button>
            <BrandLogo size={30} />
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-3xl px-6 py-16">
        <div className="mb-10">
          <div
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] font-mono uppercase tracking-[0.25em] text-white/60 mb-4"
            data-testid="privacy-badge"
          >
            <ShieldCheck size={12} /> Your data, your control
          </div>
          <h1 className="text-4xl md:text-5xl font-display font-black tracking-tight">
            Privacy Policy
          </h1>
          <p className="mt-3 text-white/50 text-sm">
            Last updated: {new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        <div className="space-y-8" data-testid="privacy-content">
          {SECTIONS.map((s) => (
            <section key={s.n} id={s.id || `section-${s.n}`}>
              <div className="flex items-start gap-3 mb-2">
                <span
                  className="shrink-0 h-7 w-7 rounded-lg flex items-center justify-center font-mono text-[12px] font-bold"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(0,240,255,0.2) 0%, rgba(255,0,127,0.2) 100%)',
                    color: '#00F0FF',
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  {s.n}
                </span>
                <h2 className="text-lg md:text-xl font-display font-black text-white leading-tight pt-0.5">
                  {s.t}
                </h2>
              </div>
              <p className="text-[14.5px] text-white/70 leading-[1.75] pl-10">{s.p}</p>
            </section>
          ))}
        </div>

        <div className="mt-16 rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-[13px] text-white/60 leading-relaxed">
          This Policy is a starting template. Please consult a lawyer to tailor it to your
          specific business, jurisdiction, and data flows.
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
